/** A geographic coordinate using the same `lat`/`lng` naming as the GraphQL schema. */
export interface LatLng {
  lat: number
  lng: number
}

/**
 * Axis-aligned bounding box that encloses a set of coordinates.
 * `centerLat`/`centerLng` are the arithmetic midpoints — not a weighted centroid.
 */
export interface BoundingBox {
  minLat: number
  maxLat: number
  minLng: number
  maxLng: number
  centerLat: number
  centerLng: number
}

/**
 * Compute the smallest bounding box that contains every coordinate in `coords`.
 *
 * Used by both platform OrderMap implementations to fit the camera so all
 * map pins are visible.
 *
 * @throws {Error} when `coords` is empty.
 */
export function getBoundsForCoordinates(coords: LatLng[]): BoundingBox {
  if (coords.length === 0) {
    throw new Error('getBoundsForCoordinates requires at least one coordinate')
  }

  let minLat = coords[0].lat
  let maxLat = coords[0].lat
  let minLng = coords[0].lng
  let maxLng = coords[0].lng

  for (const { lat, lng } of coords) {
    if (lat < minLat) minLat = lat
    if (lat > maxLat) maxLat = lat
    if (lng < minLng) minLng = lng
    if (lng > maxLng) maxLng = lng
  }

  return {
    minLat,
    maxLat,
    minLng,
    maxLng,
    centerLat: (minLat + maxLat) / 2,
    centerLng: (minLng + maxLng) / 2,
  }
}

/**
 * Haversine distance between two coordinates in **metres**.
 *
 * Accurate to ≈ 0.5 % for distances up to ~500 km (suitable for last-mile
 * delivery routing). Does not account for Earth's oblate spheroid shape.
 */
export function getDistanceBetweenPoints(a: LatLng, b: LatLng): number {
  const R = 6_371_000 // mean Earth radius in metres
  const dLat = toRadians(b.lat - a.lat)
  const dLng = toRadians(b.lng - a.lng)
  const sinHalfLat = Math.sin(dLat / 2)
  const sinHalfLng = Math.sin(dLng / 2)
  const h =
    sinHalfLat * sinHalfLat +
    Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * sinHalfLng * sinHalfLng
  return R * 2 * Math.asin(Math.sqrt(h))
}

/**
 * Format a distance in metres as a locale-aware string.
 *
 * - `en-US` → miles ("1.5 mi")
 * - All other locales → kilometres ("2.4 km")
 */
export function formatDistance(meters: number, locale: string): string {
  if (locale === 'en-US') {
    const miles = meters / 1_609.344
    return new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: 'mile',
      unitDisplay: 'short',
      maximumFractionDigits: 1,
    }).format(miles)
  }

  const km = meters / 1_000
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'kilometer',
    unitDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(km)
}

/**
 * Generate `steps` evenly-spaced points along a polyline defined by `waypoints`.
 *
 * Points are distributed uniformly across all segments (not weighted by
 * segment length). The first and last waypoints are always included.
 *
 * @param waypoints - At least two coordinates defining the route.
 * @param steps     - Total number of output points (must be ≥ 2 to be useful).
 *
 * Returns a copy of `waypoints` unchanged when fewer than two waypoints are
 * supplied or when `steps ≤ 1`.
 */
export function interpolateRoute(waypoints: LatLng[], steps: number): LatLng[] {
  if (waypoints.length === 0) return []
  if (waypoints.length === 1 || steps <= 1) return [...waypoints]

  const result: LatLng[] = []
  const intervals = steps - 1

  for (let i = 0; i < steps; i++) {
    const t = i / intervals // 0 → 1 inclusive
    const scaled = t * (waypoints.length - 1)
    const segIdx = Math.min(Math.floor(scaled), waypoints.length - 2)
    const segT = scaled - segIdx

    const start = waypoints[segIdx]
    const end = waypoints[segIdx + 1]

    result.push({
      lat: start.lat + (end.lat - start.lat) * segT,
      lng: start.lng + (end.lng - start.lng) * segT,
    })
  }

  return result
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}
